import { Injectable, signal, inject, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { PartnerApiService } from './partner-api.service';
import { StorageService } from './storage.service';
import { MixpanelService } from './mixpanel.service';

export interface PartnerUser {
  id: string;
  name: string;
  email: string;
  joinedDate: string;
  status: string;
  portfolio: string;
  txCount: number;
}

export interface PartnerTransaction {
  txId: string;
  user: string;
  date: string;
  amount: string;
  type: string;
  status: string;
  block: string;
}

export interface DashboardStats {
  totalUsers?: number;
  portfolioValue?: number;
  totalTransactions?: number;
  taxableAmount?: number;
}

export interface DashboardChartData {
  day: string;
  depositWithdrawal: number;
  clearing: number;
}

export interface DashboardGatewayNode {
  name: string;
  ip: string;
  status: string;
  latency: number | null;
}

export interface DashboardPayload {
  stats?: DashboardStats;
  chartData?: DashboardChartData[];
  gatewayNodes?: DashboardGatewayNode[];
  hasData?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PartnerService {
  private readonly partnerApiService = inject(PartnerApiService);
  private readonly storageService = inject(StorageService);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly mixpanel = inject(MixpanelService);
  private sessionTimeoutHandled = false;
  private isFetchingDashboard = false;
  private lastFetchedTime = 0;
  private readonly DASHBOARD_CACHE_KEY = 'partner_dashboard_cache';

  // Theme state (default light)
  readonly isDarkMode = signal(false);

  // Dashboard Session State.
  // The session JWT now lives in an httpOnly cookie (set by the backend on
  // login) which JS cannot read, so login state is tracked with a separate
  // non-sensitive flag. A 401 from any call is the authoritative "logged out"
  // signal (handled in the interceptor -> handleSessionTimeout).
  readonly isLoggedIn = signal(!!this.storageService.getItem('partner_logged_in'));
  readonly loginNodeId = signal(this.storageService.getItem('partner_node_id') || '');
  readonly companyName = signal(this.storageService.getItem('partner_profile_name') || 'Hashbooks Global Partner');
  readonly companyEmail = signal(this.storageService.getItem('partner_profile_email') || 'contact@hashbooks.io');

  // CSRF token — kept in memory only (mirrors the gaulbridge flow). The signed
  // counterpart lives in an httpOnly cookie the JS cannot read, so on reload we
  // re-fetch a usable token via ensureCsrfReady().
  readonly csrfToken = signal<string>('');
  private csrfBootstrapPromise: Promise<void> | null = null;

  constructor() {
    // Load saved theme preference (default to light mode if not set)
    const savedTheme = this.storageService.getItem('partner_theme');
    const isDark = savedTheme === 'dark';
    this.isDarkMode.set(isDark);
    if (isPlatformBrowser(this.platformId)) {
      this.document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    }

    if (this.isLoggedIn()) {
      // Instantly paint the last-known dashboard so a hard refresh shows data
      // immediately instead of zeros/blank while the network request is in flight.
      this.restoreDashboardCache();
      // Warm the CSRF token (lost from memory on reload) before any mutating call.
      this.ensureCsrfReady();
      this.fetchProfile();
      this.fetchDashboardData();
    }
  }

  /**
   * Ensure a CSRF token is available in memory. Resolves immediately when one is
   * already held; otherwise fetches a fresh token (and re-sets the cookie),
   * de-duplicating concurrent callers via a single in-flight promise.
   */
  ensureCsrfReady(): Promise<void> {
    if (this.csrfToken()) {
      return Promise.resolve();
    }
    this.csrfBootstrapPromise ??= firstValueFrom(this.partnerApiService.getCsrfToken())
      .then((res) => {
        const token = res?.data?.csrf_token;
        if (token) {
          this.csrfToken.set(token);
        }
      })
      .catch(() => {})
      .finally(() => {
        this.csrfBootstrapPromise = null;
      });
    return this.csrfBootstrapPromise;
  }

  // Toast notifications
  readonly toastMessage = signal('');
  readonly isToastOpen = signal(false);
  readonly toastType = signal<'success' | 'error' | 'info'>('success');
  private toastTimeoutId: any = null;

  // Dashboard Stats
  stats = {
    totalUsers: '0',
    portfolioValue: '$0.00',
    totalTransactions: '0',
    taxableAmount: '$0.00'
  };

  readonly chartData = signal<any[]>([]);
  readonly gatewayNodes = signal<any[]>([]);
  readonly hasChartData = signal<boolean>(false);

  // Connected Users List
  readonly users = signal<PartnerUser[]>([
    { id: 'usr_92a1', name: 'John Doe', email: 'john@example.com', joinedDate: '2025-10-12', status: 'Active', portfolio: '$420,950', txCount: 148 },
    { id: 'usr_31b8', name: 'Alice Smith', email: 'alice@example.com', joinedDate: '2025-11-05', status: 'Active', portfolio: '$1,290,000', txCount: 890 },
    { id: 'usr_74c3', name: 'Bob Johnson', email: 'bob@example.com', joinedDate: '2025-12-01', status: 'Suspended', portfolio: '$0', txCount: 42 },
    { id: 'usr_52d9', name: 'Emma Wilson', email: 'emma@example.com', joinedDate: '2026-01-15', status: 'Active', portfolio: '$3,405,100', txCount: 2315 },
    { id: 'usr_83e2', name: 'Michael Brown', email: 'michael@example.com', joinedDate: '2026-02-28', status: 'Active', portfolio: '$820,400', txCount: 312 },
    { id: 'usr_14f5', name: 'Sophia Davis', email: 'sophia@example.com', joinedDate: '2026-03-10', status: 'Pending', portfolio: '$150,000', txCount: 14 },
    { id: 'usr_62g7', name: 'James Miller', email: 'james@example.com', joinedDate: '2026-04-02', status: 'Active', portfolio: '$5,980,000', txCount: 4120 }
  ]);

  // Transactions Master List
  readonly transactions = signal<PartnerTransaction[]>([
    { txId: 'tx_hb_91823', user: 'Emma Wilson', date: '2026-05-18 14:22:15', amount: '$42,500.00', type: 'Deposit', status: 'Completed', block: '#491,920' },
    { txId: 'tx_hb_82713', user: 'James Miller', date: '2026-05-18 13:05:42', amount: '$150,000.00', type: 'Clearing', status: 'Completed', block: '#491,902' },
    { txId: 'tx_hb_73625', user: 'Alice Smith', date: '2026-05-17 11:40:10', amount: '$12,400.00', type: 'Withdrawal', status: 'Completed', block: '#491,512' },
    { txId: 'tx_hb_64731', user: 'John Doe', date: '2026-05-17 09:12:05', amount: '$3,800.00', type: 'Deposit', status: 'Completed', block: '#491,480' },
    { txId: 'tx_hb_58210', user: 'James Miller', date: '2026-05-16 16:48:30', amount: '$95,000.00', type: 'Clearing', status: 'Completed', block: '#491,102' },
    { txId: 'tx_hb_49201', user: 'Bob Johnson', date: '2026-05-15 10:20:15', amount: '$1,200.00', type: 'Withdrawal', status: 'Failed', block: '#490,582' },
    { txId: 'tx_hb_38192', user: 'Emma Wilson', date: '2026-05-14 15:30:22', amount: '$250,000.00', type: 'Clearing', status: 'Processing', block: '#490,129' },
    { txId: 'tx_hb_29103', user: 'Michael Brown', date: '2026-05-13 08:05:11', amount: '$8,900.00', type: 'Deposit', status: 'Completed', block: '#489,890' },
    { txId: 'tx_hb_18293', user: 'Sophia Davis', date: '2026-05-12 14:15:00', amount: '$15,000.00', type: 'Deposit', status: 'Completed', block: '#489,510' }
  ]);

  showToast(msg: string, type?: 'success' | 'error' | 'info') {
    let resolvedType = type;
    if (!resolvedType) {
      const lower = msg.toLowerCase();
      if (
        lower.includes('error') || 
        lower.includes('fail') || 
        lower.includes('invalid') || 
        lower.includes('not match') || 
        lower.includes('cannot') ||
        lower.includes('please enter')
      ) {
        resolvedType = 'error';
      } else {
        resolvedType = 'success';
      }
    }
    this.toastMessage.set(msg);
    this.toastType.set(resolvedType);
    this.isToastOpen.set(true);
    if (this.toastTimeoutId) {
      clearTimeout(this.toastTimeoutId);
    }
    this.toastTimeoutId = setTimeout(() => {
      this.isToastOpen.set(false);
      this.toastTimeoutId = null;
    }, 4000);
  }

  closeToast() {
    this.isToastOpen.set(false);
    if (this.toastTimeoutId) {
      clearTimeout(this.toastTimeoutId);
      this.toastTimeoutId = null;
    }
  }

  toggleTheme() {
    this.isDarkMode.set(!this.isDarkMode());
    const theme = this.isDarkMode() ? 'dark' : 'light';
    if (isPlatformBrowser(this.platformId)) {
      this.document.documentElement.setAttribute('data-theme', theme);
    }
    this.storageService.setItem('partner_theme', theme);
  }

  handleLogout() {
    this.partnerApiService.logout().subscribe({
      next: () => {},
      error: () => {}
    });

    this.mixpanel.reset();
    this.clearSessionState();

    this.showToast('Logged out of partner node successfully.');
    this.router.navigateByUrl('/');
  }

  handleEnterConsole(nodeId: string, _token?: string, csrf?: string) {
    this.sessionTimeoutHandled = false;
    this.loginNodeId.set(nodeId);
    this.storageService.setItem('partner_node_id', nodeId);
    // Auth rides the httpOnly session cookie; we only persist a login flag.
    this.storageService.setItem('partner_logged_in', '1');
    if (csrf) {
      this.csrfToken.set(csrf);
    }
    this.isLoggedIn.set(true);
    // Drop any dashboard cache left over from a previous account before fetching
    // this partner's fresh data, so account switches never reuse stale metrics.
    this.storageService.removeItem(this.DASHBOARD_CACHE_KEY);
    this.fetchProfile();
    this.fetchDashboardData();
  }

  fetchProfile() {
    this.partnerApiService.getProfile().subscribe({
      next: (res) => {
        if (res && res.data) {
          const data = res.data;
          this.companyName.set(data.company_name || 'Hashbooks Global Partner');
          this.companyEmail.set(data.email || 'contact@hashbooks.io');
          this.storageService.setItem('partner_profile_name', data.company_name || '');
          this.storageService.setItem('partner_profile_email', data.email || '');
          this.storageService.setItem('partner_profile_phone', data.phone || '');
          this.storageService.setItem('partner_profile_address', data.address || '');
          this.storageService.setItem('partner_profile_country', data.country || '');
          this.storageService.setItem('partner_profile_company_id', data.company_identity_number || '');
          this.storageService.setItem('partner_profile_joined_date', data.created_at ? data.created_at.substring(0, 10) : '2025-10-12');
        }
      },
      error: () => {}
    });
  }

  fetchDashboardData(force = false) {
    const now = Date.now();
    if (this.isFetchingDashboard) {
      return;
    }
    if (!force && now - this.lastFetchedTime < 5000) {
      return;
    }
    this.isFetchingDashboard = true;

    this.partnerApiService.getDashboardStats().subscribe({
      next: (res) => {
        this.isFetchingDashboard = false;
        this.lastFetchedTime = Date.now();
        const data = res?.data as DashboardPayload;
        
        const stats = data?.stats ?? {};
        const chart = Array.isArray(data?.chartData) ? data.chartData : [];
        const nodes = Array.isArray(data?.gatewayNodes) ? data.gatewayNodes : [];

        const totalUsers = Number(stats.totalUsers ?? 0);
        const portfolioValue = Number(stats.portfolioValue ?? 0);
        const totalTransactions = Number(stats.totalTransactions ?? 0);
        const taxableAmount = Number(stats.taxableAmount ?? 0);

        const formatCurrency = (val: number) => {
          return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
        };
        const formatNumber = (val: number) => {
          return new Intl.NumberFormat('en-US').format(val);
        };

        this.stats = {
          totalUsers: formatNumber(totalUsers),
          portfolioValue: formatCurrency(portfolioValue),
          totalTransactions: formatNumber(totalTransactions),
          taxableAmount: formatCurrency(taxableAmount)
        };

        const maxVol = Math.max(
          ...chart.map((d) => Math.max(Number(d.depositWithdrawal ?? 0), Number(d.clearing ?? 0))),
          1
        );

        const formatVolume = (val: number) => {
          if (val >= 1000000) {
            return (val / 1000000).toFixed(1) + 'M';
          }
          if (val >= 1000) {
            return (val / 1000).toFixed(0) + 'k';
          }
          return val.toFixed(0);
        };

        const scaledChartData = chart.map((d) => {
          const depVol = Number(d.depositWithdrawal ?? 0);
          const clearVol = Number(d.clearing ?? 0);
          return {
            ...d,
            day: d.day || '',
            depositWithdrawal: depVol,
            clearing: clearVol,
            depositWithdrawalFormatted: formatVolume(depVol),
            clearingFormatted: formatVolume(clearVol),
            depositWithdrawalHeight: (depVol / maxVol) * 100,
            clearingHeight: (clearVol / maxVol) * 100
          };
        });

        this.chartData.set(scaledChartData);
        this.gatewayNodes.set(nodes);
        this.hasChartData.set(!!data?.hasData);
        this.persistDashboardCache();
      },
      error: (err) => {
        this.isFetchingDashboard = false;
        console.error('Dashboard stats fetch failed', err);

        const hasCachedData = !!this.storageService.getItem(this.DASHBOARD_CACHE_KEY);
        const isAuthError = err?.status === 401 || err?.status === 403;

        // A 401/403 is a session timeout — already handled by the interceptor with
        // its own redirect + toast, so don't double-report it here. Otherwise only
        // surface the error when the user explicitly refreshed or there is nothing
        // cached to fall back on; a normal background refresh silently keeps the
        // last-known dashboard instead of flashing an error.
        if (!isAuthError && (force || !hasCachedData)) {
          this.showToast('Failed to load dashboard metrics.', 'error');
        }

        // Only wipe to zeros when there is no cached dashboard to keep showing.
        if (!hasCachedData) {
          this.stats = {
            totalUsers: '0',
            portfolioValue: '$0.00',
            totalTransactions: '0',
            taxableAmount: '$0.00'
          };
          this.chartData.set([]);
          this.gatewayNodes.set([]);
          this.hasChartData.set(false);
        }
      }
    });
  }

  /** Paint the last-known dashboard values synchronously (instant render on reload). */
  private restoreDashboardCache(): void {
    const raw = this.storageService.getItem(this.DASHBOARD_CACHE_KEY);
    if (!raw) {
      return;
    }
    try {
      const cached = JSON.parse(raw);

      // Only paint the cache when it belongs to the partner currently logged in
      // on this browser. The payload is stamped with the node id captured at
      // login; if it's missing or doesn't match, drop it so we never show another
      // account's dashboard (partner_logged_in alone does not prove identity).
      if (!cached?.nodeId || cached.nodeId !== this.loginNodeId()) {
        this.storageService.removeItem(this.DASHBOARD_CACHE_KEY);
        return;
      }

      if (cached?.stats) {
        this.stats = cached.stats;
      }
      if (Array.isArray(cached?.chartData)) {
        this.chartData.set(cached.chartData);
      }
      if (Array.isArray(cached?.gatewayNodes)) {
        this.gatewayNodes.set(cached.gatewayNodes);
      }
      this.hasChartData.set(!!cached?.hasChartData);
    } catch {
      // Corrupt cache — discard and let the network fetch repopulate.
      this.storageService.removeItem(this.DASHBOARD_CACHE_KEY);
    }
  }

  /** Persist the freshly-loaded dashboard so the next reload can paint instantly. */
  private persistDashboardCache(): void {
    try {
      this.storageService.setItem(
        this.DASHBOARD_CACHE_KEY,
        JSON.stringify({
          nodeId: this.loginNodeId(),
          stats: this.stats,
          chartData: this.chartData(),
          gatewayNodes: this.gatewayNodes(),
          hasChartData: this.hasChartData()
        })
      );
    } catch {
      // Storage full/unavailable — non-fatal.
    }
  }

  handleSessionTimeout(): void {
    if (this.sessionTimeoutHandled) {
      return;
    }
    this.sessionTimeoutHandled = true;
    this.clearSessionState();
    this.showToast('Session timed out. Please login again.', 'error');
    this.router.navigateByUrl('/');
  }

  private clearSessionState(): void {
    this.isLoggedIn.set(false);
    this.csrfToken.set('');
    this.csrfBootstrapPromise = null;
    this.loginNodeId.set('');
    this.companyName.set('Hashbooks Global Partner');
    this.companyEmail.set('contact@hashbooks.io');

    // Reset dashboard state
    this.stats = {
      totalUsers: '0',
      portfolioValue: '$0.00',
      totalTransactions: '0',
      taxableAmount: '$0.00'
    };
    this.chartData.set([]);
    this.gatewayNodes.set([]);
    this.hasChartData.set(false);
    this.isFetchingDashboard = false;
    this.lastFetchedTime = 0;

    this.storageService.removeItem('partner_logged_in');
    this.storageService.removeItem('partner_node_id');
    this.storageService.removeItem('partner_profile_name');
    this.storageService.removeItem('partner_profile_email');
    this.storageService.removeItem('partner_profile_phone');
    this.storageService.removeItem('partner_profile_address');
    this.storageService.removeItem('partner_profile_country');
    this.storageService.removeItem('partner_profile_company_id');
    this.storageService.removeItem('partner_profile_joined_date');
    this.storageService.removeItem('partner_2fa_enabled');
    this.storageService.removeItem('partner_password_changed_at');
    this.storageService.removeItem(this.DASHBOARD_CACHE_KEY);
  }
}
